import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useToast,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react';

import CenteredAuth from 'layouts/auth/variants/Centered';
import { useNavigate } from 'react-router-dom';
import { authRepository } from 'api/authRepository';
import { z } from 'zod';
import { resetPasswordSchema, flattenZodErrors } from 'validations/Validations';

function NewPassword() {
  const toast = useToast();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const brandStars = useColorModeValue('brand.500', 'brand.400');

  const handleSubmit = async () => {
    try {
      setErrors({});
      
      resetPasswordSchema.parse({
        newPassword: password,
        confirmPassword: confirmPassword,
      });

      // Call reset API
      await authRepository.resetpassword({ token: code, newPassword: password });
      
      toast({
        title: "Success",
        description: "Your password has been reset successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      navigate("/auth/sign-in");

    } catch (error: unknown) {
      if (error instanceof z.ZodError) {
        const newErrors = flattenZodErrors(error);
        setErrors(newErrors);
        toast({
          title: "Validation Error",
          description: "Please fix the errors before submitting.",
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to reset password.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    }
  };

  return (
    <CenteredAuth
      cardTop={{ base: '140px', md: '24vh' }}
      cardBottom={{ base: '50px', lg: 'auto' }}
    >
      <Flex
        w="100%"
        maxW="max-content"
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        px={{ base: '25px', md: '0px' }}
        flexDirection="column"
      >
        <Box me="auto" mb="34px">
          <Heading
            color={textColor}
            fontSize={{ base: '3xl', md: '36px' }}
            mb="16px"
          >
            Enter Your New Password
          </Heading>
          <Text
            color={textColorSecondary}
            fontSize="md"
            w={{ base: '100%', lg: '456px' }}
            maxW="100%"
          >
            Please enter your token and set a strong new password.
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', lg: '456px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
          align="start"
        >
          <FormControl isInvalid={!!errors['token']}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Enter Your Token<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              fontSize="sm"
              type="text"
              placeholder="Enter Your OTP"
              mb="24px"
              size="lg"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </FormControl>

          <FormControl isInvalid={!!errors['newPassword']}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              New Password<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              fontSize="sm"
              type="password"
              placeholder="Enter Your New Password"
              mb="24px"
              size="lg"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <FormErrorMessage>{errors['newPassword']}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors['confirmPassword']}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Confirm Password<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              fontSize="sm"
              type="password"
              placeholder="Confirm New Password"
              mb="24px"
              size="lg"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <FormErrorMessage>{errors['confirmPassword']}</FormErrorMessage>
          </FormControl>

          <Button
            fontSize="sm"
            variant="brand"
            fontWeight="500"
            w="100%"
            h="50px"
            mb="24px"
            onClick={handleSubmit}
          >
            Save New Password
          </Button>
        </Flex>
      </Flex>
    </CenteredAuth>
  );
}

export default NewPassword;
